import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import styled from "styled-components";
import moment from "moment";
import * as colors from "../../constants/colors";

const StlHours = styled.div`
  margin-top: 20px;
  display: grid;
  gap: 12px;
`;

const StlHoursHeader = styled.div`
  font-size: 12px;
  letter-spacing: 0.06em;
  color: #8b8b8b;
`;

const StlHoursTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const StlTodayWrap = styled.div`
  display: grid;
  gap: 2px;
`;

const StlTodayStatus = styled.div`
  font-size: 16px;
  line-height: 1.3;
  color: ${colors.textDarkest};
`;

const StlTodayHours = styled.div`
  font-size: 16px;
  line-height: 1.35;
  color: ${colors.textDarkest};
`;

const StlMoreButton = styled.button`
  border: 0;
  background: transparent;
  color: ${colors.heroOrange};
  font-size: 16px;
  cursor: pointer;
  line-height: 1.2;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 0;
`;

const StlMoreText = styled.span`
  font-size: 16px;
  line-height: 1.2;
`;

const StlChevron = styled.span`
  display: inline-block;
  transform: translateY(1px)
    rotate(${(props) => (props.$open ? "180deg" : "0deg")});
  transition: transform 0.1s linear;
  font-size: 16px;
  line-height: 1.2;
  position: relative;
  bottom: ${(props) => (props.$open ? "-4px" : "4px")};
`;

const StlAllDays = styled.div`
  display: grid;
  gap: 6px;
  padding-top: 2px;
`;

const StlDayRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  font-size: 16px;
  color: ${colors.textDarkest};
`;

const StoreHours = ({ className }) => {
  const dispatch = useDispatch();
  const [showMore, setShowMore] = useState(false);
  const hours = useSelector((state) =>
    get(state, "prismic.store_hours.data", false),
  );

  const monday = get(hours, "monday[0].text");
  const tuesday = get(hours, "tuesday[0].text");
  const wednesday = get(hours, "wednesday[0].text");
  const thursday = get(hours, "thursday[0].text");
  const friday = get(hours, "friday[0].text");
  const saturday = get(hours, "saturday[0].text");
  const sunday = get(hours, "sunday[0].text");

  const weekHours = useMemo(
    () => ({
      monday,
      tuesday,
      wednesday,
      thursday,
      friday,
      saturday,
      sunday,
    }),
    [monday, tuesday, wednesday, thursday, friday, saturday, sunday],
  );

  const dayOrder = useMemo(
    () => [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ],
    [],
  );

  const todayDayKey = moment().format("dddd").toLowerCase();
  const todayHours = get(weekHours, todayDayKey, "");
  const isClosedToday = (todayHours || "").trim().toLowerCase() === "closed";
  const todayLabel = moment().format("dddd, MMM Do");
  const todayStatus = `${isClosedToday ? "Closed" : "Open"} ${todayLabel}`;
  const todayHoursLabel =
    !isClosedToday && todayHours
      ? /^from\s/i.test(todayHours)
        ? todayHours
        : `From ${todayHours}`
      : "";

  const otherDays = useMemo(
    () =>
      dayOrder
        .filter((day) => day !== todayDayKey)
        .map((day) => ({
          dayLabel: moment(day, "dddd").format("dddd"),
          value: get(weekHours, day, ""),
        })),
    [dayOrder, todayDayKey, weekHours],
  );

  useEffect(() => {
    dispatch({
      type: "PRISMIC_LOAD_SINGLE_PAGE",
      key: "store_hours",
    });
  }, [dispatch]);

  return (
    <StlHours className={className}>
      <StlHoursHeader className="font-medium">STORE HOURS</StlHoursHeader>
      <StlHoursTopRow>
        <StlTodayWrap>
          <StlTodayStatus className="font-medium">{todayStatus}</StlTodayStatus>
          {!!todayHoursLabel && (
            <StlTodayHours className="font-book">
              {todayHoursLabel}
            </StlTodayHours>
          )}
        </StlTodayWrap>
        <StlMoreButton
          className="font-medium"
          type="button"
          onClick={() => setShowMore((prev) => !prev)}
          aria-expanded={showMore}
          aria-label={showMore ? "Hide store hours" : "Show more store hours"}
        >
          <StlMoreText>{showMore ? "Less" : "More"}</StlMoreText>
          <StlChevron $open={showMore}>⌄</StlChevron>
        </StlMoreButton>
      </StlHoursTopRow>
      {showMore && (
        <StlAllDays>
          {otherDays.map(({ dayLabel, value }) => (
            <StlDayRow key={dayLabel}>
              <span className="font-book">{dayLabel}</span>
              <span className="font-book">{value || "Closed"}</span>
            </StlDayRow>
          ))}
        </StlAllDays>
      )}
    </StlHours>
  );
};

export default StoreHours;
